import { useQuery } from "@tanstack/react-query";
import httpApi from "@src/utils/httpApi";
import { QUERY_KEYS } from "@src/queries/queryKeys";

export type JingleOption = {
  id: number;
  documentId: string;
  title: string;
};

// Offer ONLY jingles explicitly flagged `visible: true`. The flag defaults to
// false, and Strapi leaves it NULL on the jingles that predate the field, so an
// unset flag means "not reviewed yet" — hidden — rather than "legacy, keep
// showing". Matching NULL as well is what kept un-flagged jingles in the picker.
// A hidden jingle can still stay assigned to a playlist — that assignment is
// read from the playlist record, not from this list. [ROVR-4178]
const VISIBLE_JINGLE_FILTER = {
  "filters[visible][$eq]": true,
} as const;

// The show's own jingle is never offered as a manual pick: a playlist with no
// jingle already falls back to it (and then to the ROVR default) at playback
// time, so listing it would only let a curator re-select what they already get
// for free. [ROVR-4036]
const excludeJingleFilter = (
  excludeDocumentId?: string | null
): Record<string, unknown> =>
  excludeDocumentId ? { "filters[documentId][$ne]": excludeDocumentId } : {};

/** Selectable (visible) jingles, optionally narrowed by a title search. */
export const useVisibleJinglesQuery = (
  search: string,
  enabled = true,
  excludeDocumentId?: string | null
) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_JINGLES, "visible", search, excludeDocumentId],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        "pagination[pageSize]": 100,
        "fields[0]": "title",
        "fields[1]": "documentId",
        ...VISIBLE_JINGLE_FILTER,
        ...excludeJingleFilter(excludeDocumentId),
      };
      if (search) {
        params["filters[title][$containsi]"] = search;
      }
      const res = await httpApi.get("/api/jingles", { params });
      return (res.data?.data as JingleOption[]) ?? [];
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });

/**
 * Whether at least one selectable jingle exists, independent of any search box.
 * Drives whether the jingle option is offered at all: if no jingle is flagged
 * visible — or the only one that is happens to be the show's own jingle, which
 * is excluded — the picker is hidden rather than rendered empty.
 */
export const useHasVisibleJinglesQuery = (
  enabled = true,
  excludeDocumentId?: string | null
) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_JINGLES, "visible", "any", excludeDocumentId],
    queryFn: async () => {
      const res = await httpApi.get("/api/jingles", {
        params: {
          "pagination[pageSize]": 1,
          "fields[0]": "documentId",
          ...VISIBLE_JINGLE_FILTER,
          ...excludeJingleFilter(excludeDocumentId),
        },
      });
      return ((res.data?.meta?.pagination?.total as number) ?? 0) > 0;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });

/**
 * The jingle attached to a show, if any. Shows own a `jingle` relation but
 * jingles have no inverse field, so the only way to know which jingle to leave
 * out of a playlist's picker is to read it off the show. Resolves to `null`
 * when the show has no jingle.
 */
export const useShowJingleQuery = (
  showDocumentId?: string | null,
  enabled = true
) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_SHOW_JINGLE, showDocumentId],
    queryFn: async () => {
      const res = await httpApi.get(`/api/shows/${showDocumentId}`, {
        params: {
          "fields[0]": "documentId",
          "populate[jingle][fields][0]": "documentId",
        },
      });
      return (res.data?.data?.jingle?.documentId as string | undefined) ?? null;
    },
    enabled: enabled && !!showDocumentId,
    staleTime: 1000 * 60 * 5,
  });
